import { Component, inject, Input } from '@angular/core';
import { AccountSession, AccountType } from '../account.type';
import { AccountService } from '../account.service';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { CreateLogin } from '../create-login/create-login';
import { MatSnackBar } from '@angular/material/snack-bar';
import { distinctUntilChanged, EMPTY, fromEvent, map, startWith, switchMap, timer } from 'rxjs';
import { RenameDialog } from '../../shared/dialogs/rename-dialog/rename-dialog';
import { ScreenshotPreview } from '../screenshot-preview/screenshot-preview';
import { AccountSessionInfo } from '../account-session-info/account-session-info';
import { AccountPlayersDialog } from '../account-players-dialog/account-players-dialog';

@Component({
  selector: 'app-account-list',
  imports: [
    CommonModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatMenuModule,
    MatTooltipModule,
  ],
  templateUrl: './account-list.html',
  styleUrl: './account-list.scss'
})
export class AccountList {
  @Input() type: AccountType = "stock";
  private _accountService = inject(AccountService);
  private _dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);

  loading: boolean = true;
  refreshing: boolean = false;
  private hasLoadedOnce = false;
  accounts: AccountSession[] = [];
  lastUpdate: number = 0;

  ngOnInit(): void {
    // this.advancedLoad();
    this.loadAccounts();
  }

  advancedLoad(): void {
    const visibility$ = fromEvent(document, 'visibilitychange').pipe(
      startWith(null),                 // kick-off once at start
      map(() => !document.hidden),     // true  → visible
      distinctUntilChanged()           // emit only on change
    );

    visibility$
      .pipe(
        switchMap(visible =>
          visible
            ? timer(0, 30_000)         // immediate + every 30 s
            : EMPTY                    // pause while hidden
        ),
        switchMap(async () => this.loadAccounts()),
      )
      .subscribe();

    visibility$
      .pipe(
        switchMap(visible =>
          visible ? timer(0, 1_000) : EMPTY
        )
      )
      .subscribe(() => {
        this.lastUpdate++;
      });
  }

  loadAccounts() {
    if (!this.hasLoadedOnce) {
      this.loading = true;
    }
    this.refreshing = true;
    this._accountService.getAccounts(this.type).subscribe({
      next: res => {
        this.accounts = res.sessions ?? [];
        this.loading = false;
        this.refreshing = false;
        this.hasLoadedOnce = true;
      },
      error: () => {
        this.loading = false;
        this.refreshing = false;
        this.hasLoadedOnce = true;
      }
    });
  }

  openCreateLogin() {
    const dialogRef = this._dialog.open(CreateLogin, {
      maxWidth: '500px',
      disableClose: true,
      data: this.type,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadAccounts();
      }
    });
  }

  renameAccount(account: AccountSession) {
    const dialogRef = this._dialog.open(RenameDialog, {
      maxWidth: 'unset',
      data: {
        title: 'Rename Account Session',
        currentName: account.name,
        placeholder: 'Enter new account name'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this._accountService.rename(account.id, result).subscribe(
          {
            next: res => {
              this.loadAccounts();
              this._snackBar.open("Renamed successfully", "Close", { duration: 5000 });
            },
            error: err => {
              this._snackBar.open("Error renaming", "Close", { duration: 5000 });
            },
          });
      }
    });

  }

  ensureSidValidation(account: AccountSession) {
    this._accountService.ensureValidation(account.id).subscribe(
      {
        next: res => {
          this.loadAccounts();
          this._snackBar.open("Ensured validation successfully", "Close", { duration: 5000 });
        },
        error: err => {
          this._snackBar.open("Error ensuring validation", "Close", { duration: 5000 });
        },
      })
  }

  reload(account: AccountSession) {
    this._accountService.reload(account.id).subscribe(
      {
        next: res => {
          this.loadAccounts();
          this._snackBar.open("Reloaded successfully", "Close", { duration: 5000 });
        },
        error: err => {
          this._snackBar.open("Error reloading", "Close", { duration: 5000 });
        },
      })
  }

  quickSellAll(account: AccountSession) {
    this._accountService.quickSellAll(account.id).subscribe(
      {
        next: res => {
          this._snackBar.open("Quick sell started", "Close", { duration: 5000 });
          // Fire-and-forget job; re-fetch after a delay to pick up updated credit
          setTimeout(() => this.loadAccounts(), 5000);
        },
        error: err => {
          this._snackBar.open("Error starting quick sell", "Close", { duration: 5000 });
        },
      })
  }

  clearExpiredItems(account: AccountSession) {
    this._accountService.clearExpiredItems(account.id).subscribe(
      {
        next: res => {
          this._snackBar.open("Clearing expired items…", "Close", { duration: 5000 });
          // Fire-and-forget job; re-fetch after a delay to pick up updated credit
          setTimeout(() => this.loadAccounts(), 5000);
        },
        error: err => {
          this._snackBar.open("Error clearing expired items", "Close", { duration: 5000 });
        },
      })
  }

  logout(account: AccountSession) {
    this._accountService.logout(account.id).subscribe(
      {
        next: res => {
          this.loadAccounts();
          this._snackBar.open("Logged out successfully", "Close", { duration: 5000 });
        },
        error: err => {
          this._snackBar.open("Error logging out", "Close", { duration: 5000 });
        },
      })
  }

  copy(account: AccountSession) {
    var inp = document.createElement('input');
    document.body.appendChild(inp);
    inp.value = account.sid;
    inp.select();
    document.execCommand('copy', false);
    inp.remove();
  }

  loadAccountInfo(account: AccountSession) {
    account.creditLoading = true;
    account.creditFailed = false;
    this._accountService.getUserInfo(account.id).subscribe({
      next: res => {
        account.current_credit = res.credits;
        account.creditLoading = false;
      },
      error: err => {
        account.creditLoading = false;
        account.creditFailed = true;
      }
    });
  }

  suggestedAmount(credit: number) {
    const suggestedAmount = credit / 120 * 100 - 1000;
    return Math.floor(suggestedAmount / 1000);
  }

  getScreenshot(account: AccountSession, from?: 'credit' | 'home' | 'transfer' | 'current' | 'setting') {
    const sourceLabel = from ? ` (${from})` : '';
    this._snackBar.open(`Getting screenshot${sourceLabel}...`, undefined, { duration: 2000 });

    this._accountService.getScreenshot(account.id, from).subscribe({
      next: (blob: Blob) => {
        const imageUrl = URL.createObjectURL(blob);

        const dialogRef = this._dialog.open(ScreenshotPreview, {
          maxWidth: '90vw',
          maxHeight: '90vh',
          data: {
            imageUrl: imageUrl,
            imageBlob: blob
          }
        });

        dialogRef.afterClosed().subscribe(() => {
          URL.revokeObjectURL(imageUrl);
        });
      },
      error: err => {
        console.error('Error getting screenshot:', err);
        this._snackBar.open('Error getting screenshot', 'Close', { duration: 3000 });
      }
    });
  }

  showAccountInfo(account: AccountSession) {
    this._dialog.open(AccountSessionInfo, {
      maxWidth: '800px',
      data: { account }
    });
  }

  showPlayers(account: AccountSession) {
    this._dialog.open(AccountPlayersDialog, {
      maxWidth: '950px',
      data: { account }
    });
  }
}
